/// Marine-diesel maintenance — the procedures every Day Skipper +
/// power-cruiser owner should be able to perform. Goes beyond the
/// daily WOBBLE check (covered in @nav-games/sailing) into the
/// breakdown-recovery + scheduled-service routines.
///
/// Eight procedures, each with a typical-fault description, tool
/// list, ordered steps, and pitfall list. Plus a fault-symptom
/// catalogue that maps observed symptoms to the procedure that
/// fixes them.
///
/// Pure data; UI lives in apps/web.

export type ProcedureId =
  | "diesel-bleed"
  | "impeller-change"
  | "raw-water-strainer"
  | "belt-tension"
  | "oil-and-filter"
  | "fuel-filter"
  | "winterisation"
  | "spring-recommission"
  | "battery-care";

export type ProcedureCategory =
  | "breakdown"
  | "service"
  | "seasonal"
  | "electrical";

export interface Procedure {
  id: ProcedureId;
  name: string;
  category: ProcedureCategory;
  /// One-line gist.
  summary: string;
  /// What triggers this procedure — symptom or schedule.
  whenToDo: string;
  /// Tools + parts you'll need at the engine bay.
  tools: readonly string[];
  /// Step-by-step procedure in the order to execute.
  steps: readonly string[];
  /// Common mistakes + ways the procedure fails.
  pitfalls: readonly string[];
  /// Typical time to complete (in benign conditions).
  durationMinutes: string;
  /// Skill level required.
  skillLevel: "basic" | "intermediate" | "advanced";
}

export const PROCEDURES: readonly Procedure[] = Object.freeze([
  {
    id: "diesel-bleed",
    name: "Bleed the fuel system",
    category: "breakdown",
    summary:
      "Purge air from the fuel system after fuel starvation, a filter change, or running the tank dry. Diesels won't run with air in the lines.",
    whenToDo:
      "Engine stops + won't restart after running out of fuel; after changing the primary or secondary fuel filter; after work on the injection pump or injectors.",
    tools: [
      "Spanner (typically 14 mm + 17 mm) sized for the bleed nipples",
      "Absorbent rags + sealed container for spilled diesel",
      "Spare filter sealing washers",
      "Hand-pump priming lever (on most modern lift pumps) or manual primer",
      "Gloves + eye protection (high-pressure fuel can squirt)",
    ],
    steps: [
      "Identify the LOW-pressure side of the fuel system: tank → primary filter → lift pump → secondary filter → injection pump.",
      "Identify each bleed nipple — typically one on the secondary filter housing, one on the injection pump.",
      "Open the bleed nipple on the SECONDARY (engine-mounted) filter housing — slacken, don't fully remove.",
      "Operate the manual lever on the lift pump (or a manual priming bulb) — pump until diesel flows clear of bubbles from the bleed point.",
      "Tighten that bleed nipple.",
      "Repeat at the injection-pump bleed point — pump until clear flow, then tighten.",
      "Crank the engine briefly — if it doesn't fire after 10 seconds, the high-pressure side may need bleeding (loosen one injector pipe at the injector, crank again, retighten).",
      "Once running, leave on idle for 2-3 minutes to clear residual air; check no fuel leaks at any joint you opened.",
    ],
    pitfalls: [
      "Forgetting to close a bleed nipple — air gets sucked back in + you can't restart.",
      "Cranking the engine for too long — flattens the start battery + cooks the starter motor. Crank 10s, rest 30s.",
      "Loosening the high-pressure pipe under load — high-pressure diesel can inject into skin (medical emergency). Always bleed only at the dedicated low-pressure bleed nipples first.",
      "Spilling diesel onto a hot exhaust — fire risk. Catch all spills with rags + a tray.",
    ],
    durationMinutes: "20–40 minutes",
    skillLevel: "intermediate",
  },
  {
    id: "impeller-change",
    name: "Change the raw-water pump impeller",
    category: "breakdown",
    summary:
      "Replace the rubber impeller in the raw-water pump. Most common breakdown — failed impeller = no engine cooling water = overheat in minutes.",
    whenToDo:
      "No water out of exhaust; high engine-temperature alarm; planned service annually (or every 2 years for low-hour engines). Always carry a SPARE impeller + cover gasket.",
    tools: [
      "Spare impeller + cover gasket / O-ring",
      "Screwdriver (typically Phillips) or socket for the pump cover screws",
      "Long-nose pliers or impeller puller",
      "Marine grease or glycerine (NOT engine oil) to lubricate the new impeller",
      "Replacement raw-water cover screws if originals are corroded",
      "Bucket to catch dripping water",
      "Headtorch + rags",
    ],
    steps: [
      "CLOSE the raw-water sea-cock — STOP the inlet before you open anything. (Forgetting this floods the engine bay.)",
      "Place a bucket or rag tray under the pump.",
      "Remove the pump cover plate screws — keep them organised; common is six small screws.",
      "Lever the cover off carefully — note the orientation of the gasket / O-ring.",
      "Note the impeller's rotation direction (vanes curve in the direction of normal rotation) — fit the new one the same way.",
      "Grip the old impeller with long-nose pliers or impeller puller; pull straight out from the shaft.",
      "INSPECT the housing — look for broken vanes inside the housing; if vanes are missing, search the heat exchanger downstream (they can block it).",
      "Lubricate the new impeller's vanes with marine grease / glycerine — NEVER engine oil (degrades the rubber).",
      "Press the new impeller into the housing, vanes curved the correct way; rotate by hand so all vanes seat into the housing curve.",
      "Fit a new gasket / O-ring (NEVER reuse the old one — it'll leak).",
      "Replace the cover + tighten screws diagonally in a star pattern, even pressure.",
      "OPEN the sea-cock. Inspect for drips at the cover.",
      "Start the engine. Check water flow at the exhaust within 30 seconds.",
    ],
    pitfalls: [
      "Forgetting to close the sea-cock — water floods the engine bay and the gland soaks through the cabin sole.",
      "Reusing the old gasket — invisible leak that drips raw water into the bilge + corrodes everything below it.",
      "Fitting the impeller the wrong way round — it works but vanes wear backwards + fail within hours.",
      "Lost vanes left in the system — they migrate downstream + block the heat exchanger / muffler. ALWAYS account for every vane.",
      "Lubricating with engine oil — the rubber swells + fails in days.",
    ],
    durationMinutes: "15–30 minutes",
    skillLevel: "basic",
  },
  {
    id: "raw-water-strainer",
    name: "Service the raw-water strainer",
    category: "service",
    summary:
      "Clean the strainer basket that traps weed + plastic + jellyfish before they reach the impeller. Inspect daily; clean weekly or as needed.",
    whenToDo:
      "Daily inspection (part of WOBBLE — Water check). Clean when the basket has visible debris OR engine starts running warmer than usual OR after sailing through a kelp / weed patch.",
    tools: [
      "Rags",
      "Bucket (to catch the residual water in the housing)",
      "Spare O-ring or gasket (if pump uses one)",
      "Soft brush + fresh water for cleaning the basket",
    ],
    steps: [
      "CLOSE the raw-water sea-cock.",
      "Place a rag or bucket under the strainer.",
      "Open the strainer cover — usually a clear plastic top secured by a single ring nut or a few screws.",
      "Lift out the strainer basket.",
      "Clean it of all weed, plastic, salt deposits — rinse in fresh water if available; brush off stubborn debris.",
      "Inspect the basket itself for cracks or holes — replace if damaged.",
      "Inspect the O-ring / gasket on the cover — replace if compressed flat or torn.",
      "Refit basket + cover; tighten cover finger-tight only (over-tightening crushes the seal).",
      "OPEN the sea-cock; check for leaks at the cover.",
      "Run engine briefly and re-check.",
    ],
    pitfalls: [
      "Forgetting to close the sea-cock first — fountain of seawater into the engine bay.",
      "Over-tightening the cover — crushes the O-ring + causes future leaks.",
      "Not replacing a worn O-ring — slow drip into the bilge.",
      "Skipping the inspection daily — the failure mode of strainer-blockage is engine overheating 15 minutes after you've started, well after you've left the marina.",
    ],
    durationMinutes: "5–10 minutes",
    skillLevel: "basic",
  },
  {
    id: "belt-tension",
    name: "Belt tension + replacement",
    category: "service",
    summary:
      "Tension or replace the alternator + water-pump drive belts. Slipping or broken belts kill charging AND engine cooling at once.",
    whenToDo:
      "Annual inspection. Tension check daily as part of WOBBLE (Belts). Replace at first signs of fraying / cracking / glazing — and keep ONE spare belt of each size on board.",
    tools: [
      "Spanner set (12, 14, 17 mm typical for adjustment bolts)",
      "Pry bar or piece of wood for levering",
      "Replacement belt(s) of the correct size — check the engine manual for part numbers",
      "Belt-tension gauge (optional; thumb-deflection method works too)",
    ],
    steps: [
      "Engine STOPPED + cooled (belts swing freely + you don't burn yourself).",
      "Inspect both belts — check for fraying, cracking, polished glazing, loss of V-shape.",
      "TENSION CHECK: press the belt's longest unsupported span with your thumb — should deflect ~10 mm under firm pressure.",
      "To adjust: slacken the alternator pivot bolt + the adjusting strap bolt.",
      "Lever the alternator AWAY from the engine (away increases tension); hold in place.",
      "Tighten the adjusting strap bolt first, then the pivot bolt.",
      "Re-check deflection — adjust if needed.",
      "To replace: slacken both bolts, allow alternator to pivot toward engine, slip old belt off.",
      "Fit new belt round the crankshaft pulley, water-pump pulley, alternator pulley, in the correct order (check the routing diagram on the engine).",
      "Lever the alternator out to tension; tighten bolts.",
      "Run engine 5 minutes + recheck tension (belts stretch after first running).",
    ],
    pitfalls: [
      "Belt too LOOSE: slips under alternator load → no charging + glazes the belt + slips again. New belt within hours.",
      "Belt too TIGHT: bearings stress (alternator bearing first), shortens both belt + bearing life.",
      "Levering on the alternator's pulley or stator — distorts the body. Lever from a designed lug or use a wooden bar against the case.",
      "Wrong belt size — slips or won't fit at all. Buy + carry one spare for EACH size on board.",
    ],
    durationMinutes: "10–20 minutes for tension; 20–40 minutes for replacement",
    skillLevel: "basic",
  },
  {
    id: "oil-and-filter",
    name: "Engine oil + filter change",
    category: "service",
    summary:
      "Drain the engine oil + replace the oil filter. Scheduled every 100–200 engine hours OR annually (whichever first).",
    whenToDo:
      "Manufacturer schedule (typically every 100–200 hours OR annually). Also after winterisation prep.",
    tools: [
      "Marine-spec engine oil (typically 15W-40, follow engine manual)",
      "Replacement oil filter (correct part number for your engine)",
      "Oil extractor pump (most yacht engines drain by suction from the dipstick tube — no sump plug accessible)",
      "Drain pan / bucket",
      "Filter wrench (strap or cup type sized for your filter)",
      "Rags + nitrile gloves",
      "Funnel",
    ],
    steps: [
      "Run engine to operating temperature — warm oil drains faster + carries more contaminants out.",
      "STOP the engine. Place absorbent rags under the filter + at the dipstick tube.",
      "Pump out the old oil via the extractor (push the suction tube down the dipstick tube to the sump bottom). Pump until no more oil comes up.",
      "Unscrew the OLD oil filter — expect it to be full + drip; catch in the pan. Some filters are mounted at angles that release a flush of oil when removed.",
      "Wipe the filter mounting surface clean with a rag.",
      "Lightly oil the NEW filter's O-ring with fresh engine oil (NOT grease).",
      "Screw the new filter on by HAND ONLY until the gasket touches, then ½ to ¾ turn more — over-tightening cracks the housing.",
      "Refill engine via the filler cap with the specified quantity of new oil. Check the manual; typical 35 ft yacht engine takes 3–5 litres.",
      "Replace the filler cap.",
      "Start engine; idle 2–3 minutes; STOP; recheck oil level on the dipstick + top up if needed (the new filter fills with oil and lowers the visible level by ~0.5 l).",
      "Inspect for leaks at the filter mounting + the drain points.",
      "Dispose of old oil + filter at an oil-recycling point ashore (NEVER overboard — pollution offence).",
    ],
    pitfalls: [
      "Filter too tight — cracks the housing or strips the threads. Hand-tight + ½ turn only.",
      "Not lubricating the new O-ring — it tears as it tightens + leaks.",
      "Forgetting to start + recheck oil level — over-filled engine froths the oil + damages bearings.",
      "Dumping old oil in the bilge / overboard — illegal pollution offence + ruins the bilge.",
      "Wrong oil grade — using SAE 30 in a turbo-diesel rated for 15W-40 kills the engine within hours.",
    ],
    durationMinutes: "45–90 minutes",
    skillLevel: "intermediate",
  },
  {
    id: "fuel-filter",
    name: "Fuel-filter change",
    category: "service",
    summary:
      "Replace the primary (Racor / water-separator) and / or secondary (engine-mounted) fuel filter. Annual service + whenever the bowl has water.",
    whenToDo:
      "Annual scheduled service; when the Racor bowl shows water; when the engine has run lumpy under load (suggesting filter restriction); after refuelling from an unknown source.",
    tools: [
      "Replacement primary filter element (Racor R12T or similar)",
      "Replacement secondary filter cartridge (engine-specific)",
      "Spanners for the filter housings",
      "Drain bucket + rags",
      "Manual primer pump or fresh fuel in a clean container for priming",
    ],
    steps: [
      "TURN OFF the fuel valve at the tank (if fitted) to stop fuel flow.",
      "Place drain bucket under the primary filter housing.",
      "Drain the bowl: open the bottom drain valve, catch the water + sludge.",
      "Unscrew the bowl (Racor) or housing cap.",
      "Remove the old filter element; note its orientation.",
      "Wipe the housing clean of any sediment.",
      "Inspect + replace the O-ring at the bowl seal.",
      "Fit the new filter element (correct way up — arrows match the flow).",
      "Pre-fill the new bowl with clean diesel (if accessible) — speeds up re-priming.",
      "Tighten the bowl by hand — over-tightening cracks plastic.",
      "Repeat for the SECONDARY filter if scheduled — same process.",
      "OPEN the fuel valve at the tank.",
      "BLEED the fuel system — follow the bleed procedure (diesel-bleed).",
      "Start engine + check for fuel leaks at all opened joints.",
    ],
    pitfalls: [
      "Cross-threading the bowl on a Racor — easy with the plastic housing. Hand-start, then turn carefully.",
      "Forgetting to bleed afterwards — engine won't start.",
      "Refitting the wrong size or wrong-spec filter — water separator can't separate.",
      "Spilling diesel from the bowl onto the warm engine — fire risk + smell. Catch with rags.",
    ],
    durationMinutes: "30–60 minutes (including bleed)",
    skillLevel: "intermediate",
  },
  {
    id: "winterisation",
    name: "Winterise the engine",
    category: "seasonal",
    summary:
      "Prepare the engine + raw-water system to survive winter without freeze damage or corrosion. End-of-season job.",
    whenToDo:
      "End of season, before first frost. Order: fresh oil + filter, fresh fuel filter, antifreeze through raw-water system, fog cylinders.",
    tools: [
      "Engine oil + filter (do the change as part of winterisation)",
      "Antifreeze (non-toxic propylene-glycol type, sometimes called 'pink RV antifreeze')",
      "Funnel",
      "Spare bucket of clean fresh water",
      "Fogging oil (aerosol) — protects cylinder bores",
      "Spanner kit",
      "Notebook for hours-meter reading + recommissioning checklist",
    ],
    steps: [
      "OIL change FIRST — fresh oil insulates internal surfaces over the lay-up period.",
      "REPLACE the fuel filter(s) at the same visit — fresh filters start the season ahead.",
      "Top up fuel tank to 90%+ — minimises condensation inside the tank over winter.",
      "Add fuel stabiliser / biocide per maker's instruction.",
      "Run the engine to operating temperature on its normal cooling water for the last time.",
      "STOP engine; CLOSE the raw-water sea-cock.",
      "Disconnect the raw-water inlet hose from the strainer (or upstream of the pump).",
      "Place the disconnected hose into a bucket of antifreeze (50:50 propylene-glycol mix).",
      "START the engine briefly; let it draw antifreeze through the system + out the exhaust until pink antifreeze appears at the exhaust outlet.",
      "STOP engine immediately — the engine is now full of antifreeze instead of raw water.",
      "Reconnect the hose; leave the sea-cock CLOSED.",
      "FOG the cylinders: remove the air filter; spray fogging oil into the inlet manifold while cranking the engine briefly (2–3 seconds). Coats cylinder bores against rust.",
      "Disconnect the battery (or fully charge + leave on a smart maintainer).",
      "Mark the engine: 'WINTERISED + KEY OUT' tag at the helm. Note the hours-meter reading.",
    ],
    pitfalls: [
      "Forgetting to run antifreeze through — water freezes + cracks the heat exchanger or block.",
      "Using ethylene-glycol (toxic) instead of propylene-glycol (non-toxic) antifreeze — runoff into the sea is a pollution offence; the toxic version also damages some seals.",
      "Skipping the fogging step — cylinders rust over winter.",
      "Leaving the battery in winter without a smart charger — sulphates the plates + ruins the battery.",
    ],
    durationMinutes: "2–3 hours",
    skillLevel: "intermediate",
  },
  {
    id: "spring-recommission",
    name: "Spring recommissioning",
    category: "seasonal",
    summary:
      "Reverse of winterisation. Refit consumables + start the engine after the lay-up.",
    whenToDo:
      "Spring, before launching or after launching but BEFORE the first sea trip.",
    tools: [
      "New impeller (annual replacement is good practice)",
      "Belt-tension gauge or by feel",
      "Spare fuses + electrical tape",
      "Wipe rags + bucket",
    ],
    steps: [
      "Visual: walk the engine bay looking for water, oil, fuel, rodent damage, hose chafe.",
      "RECONNECT battery if disconnected. Re-time hour meter if necessary.",
      "Check engine oil level (should still be where you left it).",
      "Check coolant level in the header tank.",
      "Replace the raw-water impeller (it'll have set in shape over winter; cheap insurance against early-season failure).",
      "Inspect + tension belts.",
      "Inspect the fuel system: bowl clear, no leaks, primary filter still intact.",
      "OPEN the raw-water sea-cock.",
      "Crank the engine (with throttle in neutral + no fuel for the first crank) to circulate oil — listen for any unusual noises.",
      "Start the engine. Watch the exhaust for cooling water within 30 seconds (should see + hear it).",
      "Run at low rpm for 5–10 minutes; watch temperature; check no leaks anywhere.",
      "Run at higher rpm for another 5 minutes. Stop. Inspect for any new drips.",
      "Test all electrical loads: bilge pumps, navigation lights, instruments.",
      "Log the spring-recommissioning date + hour meter.",
    ],
    pitfalls: [
      "Forgetting to OPEN the sea-cock before starting — overheats in seconds + dry-runs the impeller (failure).",
      "Skipping the new impeller — winter-set rubber fails early in the season + the boat overheats far from help.",
      "Not running long enough to reveal slow leaks — short runs let issues hide. Run for 20+ minutes total.",
    ],
    durationMinutes: "1–2 hours",
    skillLevel: "basic",
  },
  {
    id: "battery-care",
    name: "Battery care + electrical-system check",
    category: "electrical",
    summary:
      "Inspect, test + maintain the house + start batteries; check the isolator switch, smart charger, alternator output.",
    whenToDo:
      "Annual inspection. Also when you notice the engine cranking slowly, voltage low at instruments, or after a winter on the dock.",
    tools: [
      "Multimeter (digital)",
      "Hydrometer or refractometer (for non-AGM/Lithium batteries)",
      "Wire brush + terminal cleaner",
      "Vaseline / petroleum jelly for terminal protection",
      "Distilled water (for flooded batteries)",
      "Smart charger (for slow-rate float charging)",
    ],
    steps: [
      "Inspect terminals: no corrosion, no green oxide, no looseness. Clean with wire brush if needed + smear with Vaseline.",
      "Check electrolyte level (flooded batteries only) — top up with DISTILLED water to just above plates. AGM + Lithium batteries are sealed; don't open.",
      "Voltage check at rest (no load, 12 hours after last charge): healthy 12 V battery reads 12.6 V or higher. < 12.2 V = recharge or replace.",
      "Specific gravity check with hydrometer (flooded only): 1.265 across all cells = fully charged; differences > 0.05 between cells = one cell weak, replace battery.",
      "Voltage under load check: turn on cabin lights + check voltage doesn't drop below 12 V. Drops below 11.5 V under modest load = needs replacement.",
      "Cranking check: with engine starter, voltage should briefly drop to ~10 V then recover. Drops below 9 V = battery weak.",
      "Charging check: engine running, multimeter reads 13.8–14.4 V at the battery terminals = alternator working. Reading 12.5 V or lower = alternator broken or belt slipping.",
      "Isolator switch: physically operate it through all positions (1, 2, BOTH, OFF). Should click positively. Never go to OFF with engine running (kills alternator diodes).",
      "Smart charger: confirm it's set to the right battery chemistry (AGM, GEL, Lithium, Wet). Wrong chemistry destroys the battery.",
      "Inspect main cables for chafe, corrosion at connections, melted insulation.",
    ],
    pitfalls: [
      "Turning the isolator to OFF with engine running — destroys the alternator's diodes within seconds. Expensive repair.",
      "Topping up sealed batteries — opens them up + lets electrolyte leak; battery destroyed.",
      "Mixing battery chemistries on one charger — flooded + AGM at the same charger setting kills one of them.",
      "Equalisation cycle on AGM / Lithium — over-charging will destroy these chemistries. Only flooded lead-acid can equalise.",
      "Not labelling the cabling — at panic moment with a fire, you don't know which cable goes to which.",
    ],
    durationMinutes: "30–60 minutes",
    skillLevel: "intermediate",
  },
]);

export const PROCEDURE_IDS: readonly ProcedureId[] = PROCEDURES.map(
  (p) => p.id,
);

// ───────────────────────────────────────────────────────────────────
// Fault catalogue — observable symptoms mapped to the procedure that
// fixes them. Drives the "diagnose this fault" recognition drill.
// ───────────────────────────────────────────────────────────────────

export interface EngineFault {
  id: string;
  /// Plain-English description of the symptom as the skipper observes it.
  symptom: string;
  /// The procedure that addresses this fault.
  fixedBy: ProcedureId;
  /// Why this is the right procedure.
  rationale: string;
}

export const ENGINE_FAULTS: readonly EngineFault[] = Object.freeze([
  {
    id: "no-water-exhaust",
    symptom:
      "Engine started, but no cooling water visible at the exhaust outlet after 30 seconds. Temperature climbing.",
    fixedBy: "impeller-change",
    rationale:
      "The raw-water pump impeller has likely failed or shed vanes. STOP the engine immediately + replace impeller before any sea-trial.",
  },
  {
    id: "engine-cuts-after-30-min",
    symptom:
      "Engine ran fine for 30 minutes then cut out + won't restart. Fuel gauge shows half full.",
    fixedBy: "diesel-bleed",
    rationale:
      "Air in the fuel system after the lift-pump pulled vapour from a near-empty pickup or there's an air leak on the suction side. Bleed the system + check the primary filter bowl for water.",
  },
  {
    id: "rising-temperature-after-kelp",
    symptom:
      "Engine temperature has risen 20° above normal after sailing through a kelp patch in the bay.",
    fixedBy: "raw-water-strainer",
    rationale:
      "Strainer basket clogged with weed → restricted cooling water → temperature rises. Clear the strainer.",
  },
  {
    id: "alternator-not-charging",
    symptom:
      "Voltmeter shows 12.4 V instead of 14 V with engine running. Engine sounds normal otherwise.",
    fixedBy: "belt-tension",
    rationale:
      "Alternator drive belt slipping → no charging output. Check belt tension + glaze; replace if shiny / fraying.",
  },
  {
    id: "engine-lumpy-under-load",
    symptom:
      "Engine idles smoothly but runs lumpy + loses power above 1500 rpm. Recently refuelled at an unfamiliar fuel berth.",
    fixedBy: "fuel-filter",
    rationale:
      "Contaminated fuel from the new tank → primary filter restricted. Replace filter, drain Racor bowl, bleed system.",
  },
  {
    id: "first-spring-start",
    symptom:
      "First day of the season; engine cranks but won't fire. Was winterised correctly last autumn.",
    fixedBy: "spring-recommission",
    rationale:
      "Six months without running = settled fuel, dry impeller, dry seals. Run through the spring-recommissioning checklist before troubleshooting individual faults.",
  },
  {
    id: "scheduled-100hrs",
    symptom:
      "Hour meter reads 100 hours since the last oil change. No symptoms otherwise.",
    fixedBy: "oil-and-filter",
    rationale:
      "Scheduled service. Don't let the schedule slip — modern marine-diesel oil deteriorates beyond 200 hours / 12 months and damages the engine permanently.",
  },
  {
    id: "instruments-dim",
    symptom:
      "Cabin lights dim noticeably when the fridge cuts in. Engine starts more slowly than it used to.",
    fixedBy: "battery-care",
    rationale:
      "House battery losing capacity OR poor terminal connections. Test voltage at rest + under load; clean terminals; check charging voltage.",
  },
  {
    id: "october-lay-up",
    symptom:
      "October, boat coming out of the water for winter. What's the next maintenance task?",
    fixedBy: "winterisation",
    rationale:
      "End-of-season winterisation — antifreeze through the raw-water side, fog the cylinders, top up fuel + add stabiliser, change oil.",
  },
  {
    id: "water-in-racor",
    symptom:
      "Daily WOBBLE check: clear bowl on the Racor primary filter shows a layer of water in the bottom.",
    fixedBy: "fuel-filter",
    rationale:
      "Water + diesel separating in the bowl — drain the water at the bottom valve immediately; if there's a lot, change the element + investigate the tank source.",
  },
]);
